import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Checkbox, FormControlLabel, Tooltip, Typography, withStyles } from '@material-ui/core';
import { toastr } from 'react-redux-toastr';

import { taxRateStyles } from '../styles';
import TaxRateModal from '../TaxRateModal';

import TaxesApi from '../../../api/TaxesApi';

import View from '../../common/View';
import ConfirmModal from '../../common/ConfirmModal';
import ListPagination from '../../common/ListPagination';
import TaxInUseModal from '../TaxInUseModal';
import HelpIcon from '@material-ui/icons/Info';

function TaxRatesTab({ classes, expanded, organisationId, setIsLoading, isVat0Only, setIsVat0Only }) {
    const [data, setData] = useState([]);
    const [confirmModal, setConfirmModal] = useState(false);
    const [showVat0OnlyConfirmation, setShowVat0OnlyConfirmation] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalContent, setModalContent] = useState('');
    const [selectedTaxRate, setSelectedTaxRate] = useState();
    const [isTaxRateModalVisible, setIsTaxRateModalVisible] = useState(false);
    const [archiveInUseModalVisible, setArchiveInUseModalVisible] = useState(false);
    const [itemsUsingTax, setItemsUsingTax] = useState({});
    const [method, setMethod] = useState('');
    const showArchived = useRef(false);
    const showInactive = useRef(true);

    useEffect(() => {
        const run = async () => {
            if (!data?.length && expanded) {
                try {
                    // setIsLoading(true);
                    const data = await getTaxes(
                        page,
                        rowsPerPage,
                        `archived=${showArchived.current}&inactive=${showInactive.current}`
                    );
                    setData(data);
                } catch (err) {
                    return toastr.error(err?.data?.message || 'Something went wrong');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        run();
    }, [expanded]); //eslint-disable-line

    const getTaxes = async (skip, limit, queryParam = '') => {
        try {
            // setIsLoading(true);
            const query = {
                archived: showInactive.current,
                inactive: showArchived.current
            };
            if (queryParam.indexOf('archived=true') >= 0) {
                showArchived.current = true;
                query.archived = true;
            } else {
                showArchived.current = false;
                query.archived = false;
            }
            if (queryParam.indexOf('inactive=true') >= 0) {
                showInactive.current = true;
                query.inactive = true;
            } else {
                showInactive.current = false;
                query.inactive = false;
            }

            const taxes = await TaxesApi.listTaxes(query);
            setData(taxes);
            return taxes;
        } catch (err) {
            return toastr.error(err?.data?.message || 'Something went wrong');
        } finally {
            // setIsLoading(false);
        }
    };

    const openTaxRatesModal = useCallback(taxRate => {
        if (taxRate && taxRate.id) {
            setSelectedTaxRate(taxRate);
        } else {
            setSelectedTaxRate('new');
        }
        setIsTaxRateModalVisible(true);
    }, []);

    const createTaxRate = async data => {
        try {
            // setIsLoading(true);
            await TaxesApi.create(organisationId, data);
            CloseTaxRatesModal();
            getTaxes(page, rowsPerPage, `archived=${showArchived.current}&inactive=${showInactive.current}`);
            return toastr.success('Success creating a new tax rate');
        } catch (err) {
            return toastr.error(err?.data?.message || 'Something went wrong');
        } finally {
            // setIsLoading(false);
        }
    };

    const updateTaxRate = async tax => {
        try {
            // setIsLoading(true);
            await TaxesApi.update(tax.id, tax.data);
            CloseTaxRatesModal();
            getTaxes(page, rowsPerPage, `archived=${showArchived.current}&inactive=${showInactive.current}`);
            return toastr.success(`Success updating ${tax.data.name} tax`);
        } catch (err) {
            return toastr.error(err?.data?.message || 'Something went wrong');
        } finally {
            // setIsLoading(false);
        }
    };
    const CloseTaxRatesModal = () => {
        setSelectedTaxRate();
        setIsTaxRateModalVisible(false);
    };

    const openConfirmModal = (title, component) => {
        setConfirmModal(true);
        setModalTitle(title);
        setModalContent(component);
    };
    const closeConfirmModal = () => {
        setConfirmModal(false);
        setModalTitle('');
        setModalContent();
    };

    const handleArchive = async () => {
        if (!selectedTaxRate) return;
        try {
            // setIsLoading(true);
            const tax = selectedTaxRate;
            const taxId = tax.id;
            tax.archived = !tax.archived;
            delete tax.id;
            await TaxesApi.update(taxId, tax);
            getTaxes(page, rowsPerPage, `archived=${showArchived.current}&inactive=${showInactive.current}`);
            return toastr.success(`Tax ${tax.name} successfully ${tax.archived ? 'archived' : 'unarchived'}`);
        } catch (err) {
            return toastr.error(err?.data?.message || 'Something went wrong');
        } finally {
            // setIsLoading(false);
        }
    };

    const toDo = () => {
        if (modalTitle === 'Archive Tax') handleArchive();
    };

    const openArchive = async tax => {
        setSelectedTaxRate(tax);
        const itemsUsingTax = await TaxesApi.getItemsUsingTax(tax.id);
        if (
            itemsUsingTax.services?.length > 0 ||
            itemsUsingTax.products?.length > 0 ||
            itemsUsingTax.courses?.length > 0
        ) {
            setMethod('archive');
            setArchiveInUseModalVisible(true);
            setItemsUsingTax(itemsUsingTax);
        } else {
            openConfirmModal(
                'Archive Tax',
                `Are you sure you want to ${(tax.name || {}).archived ? 'unarchive' : 'archive'} this tax?`
            );
        }
    };
    const inactivateItem = async (tax, force = false) => {
        if (isVat0Only) {
            return;
        }
        try {
            // setIsLoading(true);
            setSelectedTaxRate(tax);
            if (tax.active && !force) {
                const itemsUsingTax = await TaxesApi.getItemsUsingTax(tax.id);
                if (
                    itemsUsingTax.services?.length > 0 ||
                    itemsUsingTax.products?.length > 0 ||
                    itemsUsingTax.courses?.length > 0
                ) {
                    setMethod('inactivate');
                    setArchiveInUseModalVisible(true);
                    setItemsUsingTax(itemsUsingTax);
                    return;
                }
            }
            const taxId = tax.id;
            tax.active = !tax.active;
            delete tax.id;
            await TaxesApi.update(taxId, tax);
            getTaxes(page, rowsPerPage, `archived=${showArchived.current}&inactive=${showInactive.current}`);
            return toastr.success(`Tax ${tax.name} successfully ${tax.active ? 'activeted' : 'inactivated'}`);
        } catch (err) {
            return toastr.error(err?.data?.message || 'Something went wrong');
        } finally {
            // setIsLoading(false);
        }
    };
    const actions = [
        {
            title: 'archive',
            action: openArchive
        }
    ];
    const unselectItem = async (field, value, tax) => {
        try {
            // setIsLoading(true);
            tax.items[field] = value;
            const taxId = tax.id;
            delete tax.id;
            await TaxesApi.update(taxId, tax);
            getTaxes(page, rowsPerPage, `archived=${showArchived.current}&inactive=${showInactive.current}`);
        } catch (err) {
            return toastr.error(err?.data?.message || 'Something went wrong');
        } finally {
            // setIsLoading(false);
        }
    };

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    return (
        <div className={classes.taxRatesTabRoot}>
            <View>
                <div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isVat0Only}
                                onChange={e => {
                                    e.preventDefault();
                                    if (data.find(el => el.rate === 0)) {
                                        setShowVat0OnlyConfirmation(true);
                                    } else {
                                        toastr.error('Please create a 0% tax to use this option');
                                    }
                                }}
                                id="vat0Only"
                                name="product.autoStockControl"
                                color="primary"
                            />
                        }
                        className={`${classes.label}`}
                        style={{ marginRight: 5 }}
                        label="Use Medical / Cosmetic labels - all my sales are 0% tax"
                    />

                    <Tooltip
                        placement="right"
                        title={
                            <Typography style={{ fontSize: '14px', display: 'grid' }}>
                                This function is useful for those not registered for VAT but wanting to know the value
                                of cosmetic vs medical sales. If selected, you cannot use any tax other than 0%.
                                <br />
                                For those already registered for VAT, these labels are not required - you can change the
                                VAT on the invoice as necessary.
                            </Typography>
                        }
                        aria-label="help"
                    >
                        <HelpIcon className={classes.helpIcon} />
                    </Tooltip>
                    <Typography
                        variant="span"
                        style={{ display: 'inline-block', marginLeft: 10, position: 'relative', top: 2 }}
                    >
                        Read{' '}
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://support.collums.co/en/articles/11140610-cosmetic-medical-labels"
                        >
                            this article
                        </a>{' '}
                        for more information.
                    </Typography>
                </div>
                <ListPagination
                    isFromOrg={true}
                    page={page}
                    setPage={setPage}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    data={data}
                    maxSize={data.length}
                    defaultColumns={[
                        { title: 'Active', field: 'active', onClick: inactivateItem, disabled: isVat0Only },
                        { title: 'Name', field: 'name', onClick: openTaxRatesModal },
                        { title: 'Description', field: 'description' },
                        { title: 'Rate', field: 'rate' },
                        { title: 'Products', field: 'items.products' },
                        { title: 'Services', field: 'items.services' }
                    ]}
                    actions={actions}
                    onCreate={openTaxRatesModal}
                    filters={[
                        { name: 'archived', label: 'Show Archived', value: false },
                        { name: 'inactive', label: 'Show Inactive', value: true }
                    ]}
                    highlightArchived={true}
                    ApiCall={getTaxes}
                    activeCall={inactivateItem}
                    unselectItem={unselectItem}
                    addNewButtonText={'Add new tax rate'}
                />
            </View>
            {confirmModal && (
                <ConfirmModal
                    isOpen
                    setIsOpen={closeConfirmModal}
                    onConfirm={toDo}
                    title={modalTitle}
                    content={modalContent}
                />
            )}
            {isTaxRateModalVisible && selectedTaxRate && (
                <TaxRateModal
                    closeModal={CloseTaxRatesModal}
                    createTax={createTaxRate}
                    updateTax={updateTaxRate}
                    selectedTaxRate={selectedTaxRate}
                />
            )}
            {archiveInUseModalVisible && selectedTaxRate && itemsUsingTax && (
                <TaxInUseModal
                    method={method}
                    setModalVisible={setArchiveInUseModalVisible}
                    itemsUsingTax={itemsUsingTax}
                    tax={selectedTaxRate}
                />
            )}

            <ConfirmModal
                isOpen={showVat0OnlyConfirmation}
                hideCloseIcon={true}
                setIsOpen={value => {
                    setShowVat0OnlyConfirmation(value);
                }}
                onConfirm={() => {
                    if (!isVat0Only) {
                        setData(
                            data.map(el => {
                                if (el.active && el.rate !== 0) {
                                    return { ...el, active: false };
                                } else {
                                    return { ...el };
                                }
                            })
                        );
                        setIsVat0Only(true);
                    } else {
                        setIsVat0Only(false);
                    }
                    setShowVat0OnlyConfirmation(false);
                }}
                title={''}
                content={
                    <>
                        {!isVat0Only && (
                            <Typography>
                                If you continue, all tax rates except 0% will be disabled.
                                <br />
                                All services, products, courses and packages will now have 0% tax. If necessary, the net
                                price will be altered so that the sale price remains the same.
                            </Typography>
                        )}
                        {isVat0Only && (
                            <Typography>
                                You will now be able to add different tax rates to your services, products, courses and
                                packages.
                            </Typography>
                        )}
                    </>
                }
                centerContent
                continueText={'Continue'}
                cancelText={'Back'}
                disableBackdropClick={true}
            />
        </div>
    );
}

export default withStyles(taxRateStyles)(TaxRatesTab);
